type Artist = { id?: string | number; name?: string; platform?: string };
type ArtistTrack = { id?: string; sourceType?: string; artists?: string[]; artistIds?: (string | number)[]; metadata?: Record<string, any> };
export type ArtistNavigation = { route: { path: string; query?: Record<string, string> } | null; reason: string };
const text = (value: unknown) => String(value ?? '').trim();
const qqId = (id: string) => /^qq:artist:[A-Za-z0-9]{1,64}$/.test(id) && !['0', 'undefined', 'null'].includes(id.slice(10));

/** Native IDs are only portable with their platform; names never identify an online artist. */
export function trackArtistItems(track?: ArtistTrack | null): Artist[] {
  if (!track) return [];
  const rawPlatform = track.metadata?.platform || (track.sourceType === 'online' ? track.id?.split(':')[2] : '');
  const platform = rawPlatform === 'qq' ? 'tx' : rawPlatform;
  const ids = track.artistIds?.length ? track.artistIds : track.metadata?.artistIds || [];
  return (track.artists || []).map((name, index) => ({ name, platform,
    id: platform === 'tx' && text(ids[index]) && text(ids[index]) !== '0' ? text(ids[index]).replace(/^(?!qq:artist:)/, 'qq:artist:') : platform === 'wy' ? text(ids[index]) : '',
  }));
}

export function artistNavigation(artist: Artist, mode: string): ArtistNavigation {
  let name = text(artist.name), id = text(artist.id);
  if (!name && id.startsWith('local:')) { try { name = decodeURIComponent(id.slice(6)); } catch { name = id.slice(6); } }
  if (mode === 'local') return name ? { route: { path: '/library', query: { tab: 'artists', artist: name } }, reason: '' } : { route: null, reason: '这首歌曲没有艺术家名称。' };
  const platform = artist.platform === 'qq' ? 'tx' : artist.platform;
  if (qqId(id) && platform !== 'wy') return { route: { path: '/artist/' + encodeURIComponent(id) }, reason: '' };
  if (platform === 'tx' && /^[A-Za-z0-9]{1,64}$/.test(id) && id !== '0') id = 'qq:artist:' + id;
  else if ((!platform || platform === 'wy') && /^[1-9]\d*$/.test(id)) return { route: { path: '/artist/' + id }, reason: '' };
  if (qqId(id) && platform === 'tx') return { route: { path: '/artist/' + encodeURIComponent(id) }, reason: '' };
  return { route: null, reason: '这首歌曲没有可确认的平台歌手 ID，无法打开准确主页；可切换到本地模式查看已收录作品。' };
}

/** Artist-card playback must collect the same full song range as its page. */
export async function collectCatalogTracks<T extends { id: string }>(load: (offset: string) => Promise<{ tracks?: T[]; nextCursor?: string | number | null; more?: boolean; partial?: boolean; scope?: string; total?: number }>, label = '歌手作品'): Promise<T[]> {
  const tracks = new Map<string, T>();
  let offset = '0', total: number | null = null;
  while (true) {
    const page = await load(offset);
    if (page.partial || page.scope === 'popular') throw new Error(label + '尚未完整读取，请进入详情重试。');
    if (page.total != null && Number.isFinite(page.total)) total = Math.max(total ?? 0, Number(page.total));
    const previousSize = tracks.size;
    for (const track of page.tracks || []) if (!tracks.has(track.id)) tracks.set(track.id, track);
    if (previousSize > 0 && page.tracks?.length && tracks.size === previousSize) throw new Error(label + '分页未推进，请进入详情重试。');
    if (page.nextCursor === null || page.nextCursor === undefined) {
      if (page.more || total !== null && tracks.size < total) throw new Error(label + '尚未完整读取，请进入详情重试。');
      return [...tracks.values()];
    }
    const next = String(page.nextCursor);
    if (!/^\d+$/.test(next) || !Number.isSafeInteger(Number(next)) || Number(next) <= Number(offset) || tracks.size === previousSize) throw new Error(label + '分页未推进，请进入详情重试。');
    offset = next;
  }
}
export { collectCatalogTracks as collectArtistSongs };
